<script setup>
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useOfficeOfferStore } from '@stores/useOfficeOfferStore';
import { trans, transchoice } from '@elements/translation';
import { currencyNoDecimal, number } from '@elements/number-formatter';

const officeOfferStore = useOfficeOfferStore();
const { currentOfficeOffer, officeOffersCombinator, hasMultipleOfficeOffers } = storeToRefs(officeOfferStore);
const openSpaceCode = 'open_space';
const openSpaceType = 'Open Space';

const getCurrentOfficeOfferData = computed(() => {
  const { offerTypes, capacity, surface } = officeOffersCombinator.value;

  let offerType = offerTypes[0]?.displayOfficeType;
  let offerTypesCount = offerTypes.length;
  let offerCapacity = capacity;

  if (!officeOffersCombinator.value || !offerTypes.length) {
    offerType = currentOfficeOffer.value.display_office_type;
    offerTypesCount = 1;
    offerCapacity = currentOfficeOffer.value.capacity;
  }

  if (officeOffersCombinator.value.hashIds.length === 1 && officeOffersCombinator.value.hashIds[0] === currentOfficeOffer.value.hash_id) {
    offerCapacity = currentOfficeOffer.value.capacity;
  }

  const offerSurface = officeOffersCombinator.value && surface ? surface : currentOfficeOffer.value.surface;

  return { offerTypesCount, offerType, capacity: offerCapacity, surface: offerSurface };
});

const updateCapacityData = () => {
  const { capacity } = getCurrentOfficeOfferData.value;
  document.querySelectorAll('[data-capacity-combinator]').forEach((element) => {
    /* eslint-disable no-param-reassign */
    if (officeOfferStore.isUniqueOfType(openSpaceCode, openSpaceType)) {
      element.textContent = `${capacity} ${transchoice('global.post_office', { count: capacity })}${transchoice('global.available', { count: capacity })}`;
      return;
    }
    element.textContent = capacity + transchoice('global.post_office', { count: capacity });
    /* eslint-enable no-param-reassign */
  });
};

const updateSurfaceData = () => {
  const { surface } = getCurrentOfficeOfferData.value;

  document.querySelectorAll('[data-surface-combinator]').forEach((element) => {
    // eslint-disable-next-line no-param-reassign
    element.textContent = `${number(surface)} ${trans('global.area_unit.MTK')}`;
  });
};

const updateTypeData = () => {
  const { offerType, offerTypesCount } = getCurrentOfficeOfferData.value;
  const { offerTypes } = officeOffersCombinator.value;

  document.querySelectorAll('[data-type-combinator]').forEach((element) => {
    /* eslint-disable no-param-reassign */
    if (hasMultipleOfficeOffers.value) {
      element.textContent = `${transchoice('multioffice.navbar.choice', { count: offerTypesCount })} • `;
      element.classList.add('multiple');
      return;
    }

    element.classList.remove('multiple');
    element.textContent = offerType ? `${trans(`office_type.${offerType}`)} • ` : '';
    /* eslint-enable no-param-reassign */
  });

  document.querySelectorAll('[data-selected-offer]').forEach((element) => {
    if (hasMultipleOfficeOffers.value) {
      element.classList.add('multiple');
      return;
    }

    element.classList.remove('multiple');
  });

  const hasTypes = typeof offerTypes[0]?.displayOfficeType !== 'undefined' && typeof offerTypes[0]?.displayType !== 'undefined';
  document.querySelectorAll('[data-organization-combinator]').forEach((element) => {
    if (!hasMultipleOfficeOffers.value) {
      element.classList.remove('multiple');
      return;
    }

    element.classList.add('less-text');
    if (!hasTypes) {
      element.classList.add('hidden');
    }
  });
};

const updateModifyClass = () => {
  document.querySelectorAll('[data-modify-combinator]').forEach((element) => {
    const moreOfficeAccess = document.querySelector('[data-simple-combinator]');

    if (hasMultipleOfficeOffers.value) {
      element.classList.remove('!hidden');
      moreOfficeAccess.classList.add('!hidden');
      return;
    }

    element.classList.add('!hidden');
    moreOfficeAccess.classList.remove('!hidden');
  });
};

const updatePriceData = () => {
  const { price } = officeOffersCombinator.value;
  document.querySelectorAll('[data-price-combinator]').forEach((element) => {
    if (hasMultipleOfficeOffers.value && !officeOfferStore.isUniqueOfType(openSpaceCode, openSpaceType)) {
      // eslint-disable-next-line no-param-reassign
      element.textContent = currencyNoDecimal(price);
    }
  });
};

const updateUnitData = () => {
  document.querySelectorAll('[data-unit-combinator]').forEach((element) => {
    /* eslint-disable no-param-reassign */
    if (hasMultipleOfficeOffers.value) {
      element.textContent = ` ${trans('global.no_tax_per_month_letters')}`;
      return;
    }

    if (officeOfferStore.isUniqueOfType(openSpaceCode, openSpaceType)) {
      element.textContent = trans('global.no_tax_per_seat_per_month_letters');
    }
    /* eslint-enable no-param-reassign */
  });
};

const updateData = () => {
  if (!officeOffersCombinator.value) {
    return;
  }

  updateCapacityData();
  updateSurfaceData();
  updateTypeData();
  updateModifyClass();
  updatePriceData();
  updateUnitData();
};

watch(officeOffersCombinator, () => {
  updateData();
});
</script>

<template>
  <div />
</template>
